import React from "react"
import { connect } from "react-redux"
import {
    BaselineDataBase,
    InstructionsArgs
} from "../../BaselineDataBase/BaselineDataBase"
import { Button, Title, Text, CodeSnippet, Columns, Box } from "dash-styled"
import { routePaths } from "../../../../../../../../../../Routes"
import { NavButton } from "../../../../../../../../../Common/NavButton/NavButton"
import {
    State,
    MainThunkDispatch
} from "../../../../../../../../../../Reducers"
import { DataFileExplorer } from "../../../../../../../../../Common/DataFileExplorer/DataFileExplorer"
import { batchCreateBaselineData } from "../../../../../../../../../../Actions/API/AppInstance/AppInstanceBaselineDataActions"
import { downloadAsJSON } from "../../../../../../../../../../Helpers/Functions/downloadAs"
import { CreateFileUrlLink } from "../../../../../../../Common/CreateFileUrlLink/CreateFileUrlLink"
import { clearDataFileExplorer } from "../../../../../../../../../../Actions/Client/DataFileExplorer/AddDataActions"
import { useAsyncCommand } from "../../../../../../../../../../Hooks/useAsyncCommand"
import { push } from "connected-react-router"
import { JSONSchema7 } from "json-schema"
import { useAppTemplate } from "../../../../../../../../../../Hooks/useAppTemplate"
import { hasValue } from "../../../../../../../../../../Models/Client/FetchState"

const INPUT = (id: string) => `${id}_baseline_input`
const OUTPUT = (id: string) => `${id}_baseline_output`

type UploadData = { input: any; output: any }[]

interface UploadProps {
    orgUsername: string
    appID: string
}

interface StateProps {
    input?: any[]
    output?: any[]
}

interface DispatchProps {
    submit: (
        data: UploadData,
        inputSchema: JSONSchema7,
        outputSchema: JSONSchema7
    ) => Promise<void>
}

export type Props = UploadProps & DispatchProps & StateProps

const UploadIsolated = ({
    orgUsername,
    appID,
    submit,
    input,
    output
}: Props) => {
    const templateFetch = useAppTemplate(appID)
    const data: UploadData | undefined =
        input !== undefined
            ? input.map((item, index) => ({
                  input: item,
                  output: output && index < output.length && output[index]
              }))
            : output !== undefined
            ? output.map(item => ({
                  input: undefined,
                  output: item
              }))
            : undefined

    const render = ({ template, example, instance }: InstructionsArgs) => {
        const rows =
            data &&
            data.map((item, index) => (
                <tr key={index}>
                    <td style={{ overflow: "auto" }}>
                        <CodeSnippet>
                            {JSON.stringify(item.input, null, 4)}
                        </CodeSnippet>
                    </td>
                    <td style={{ overflow: "auto" }}>
                        <CodeSnippet>
                            {JSON.stringify(item.output, null, 4)}
                        </CodeSnippet>
                    </td>
                </tr>
            ))

        const table = rows && (
            <table
                style={{
                    maxWidth: "100%",
                    tableLayout: "fixed",
                    width: "100%"
                }}
            >
                <thead>
                    <tr>
                        <td>Input</td>
                        <td>Output</td>
                    </tr>
                </thead>
                <tbody>{rows}</tbody>
            </table>
        )

        const handleInputClick = () => {
            if (example)
                downloadAsJSON(
                    example.input,
                    `${instance.name}__sample__baseline__input__data`
                )
        }

        const downloadInput = example && (
            <Box marginBottom="5">
                <Button
                    color="darkviolet"
                    onClick={handleInputClick}
                    variant="contained"
                >
                    Download sample input JSON
                </Button>
            </Box>
        )

        const handleOutputClick = () => {
            if (example && example.output)
                downloadAsJSON(
                    example.output,
                    `${instance.name}__sample__baseline__output__data`
                )
        }

        const downloadOutput = example && (
            <Box marginBottom="5">
                <Button
                    color="darkviolet"
                    onClick={handleOutputClick}
                    variant="contained"
                >
                    Download sample output JSON
                </Button>
            </Box>
        )

        return (
            <div>
                {(!example || !example.output || !example.isValidJob) && (
                    <Text>
                        Please note the examples below are illustrative only.
                        Ground truth data created using these values may be
                        inaccurate and will lead to low quality labels and
                        erroneous measurements. To ensure you have the correct
                        structure and values for your ground truth data, you
                        will need at least one processed data point.
                    </Text>
                )}
                <Columns spacing="4" flex={true}>
                    <div style={{ width: "50%" }}>
                        <Box marginBottom="4">
                            <Title variant="heading2">Input</Title>
                            <Text>
                                Upload a JSON file matching the data programs
                                input schema to create new ground truth data.
                            </Text>
                            <DataFileExplorer
                                id={INPUT(appID)}
                                schema={template.inputSchema}
                            />
                        </Box>
                        {downloadInput}
                    </div>
                    <div style={{ width: "50%" }}>
                        <Box marginBottom="4">
                            <Title variant="heading2">Output</Title>
                            <Text>
                                Upload a JSON file matching the data programs
                                output schema to create new ground truth data.
                            </Text>
                            <DataFileExplorer
                                id={OUTPUT(appID)}
                                schema={template.outputSchema}
                            />
                        </Box>
                        {downloadOutput}
                    </div>
                </Columns>
                {table}
            </div>
        )
    }
    const { isProcessing, perform } = useAsyncCommand(
        data === undefined || !hasValue(templateFetch)
            ? undefined
            : () =>
                  submit(
                      data,
                      templateFetch.value.inputSchema,
                      templateFetch.value.outputSchema
                  )
    )

    const renderSidebar = () => {
        return (
            <div>
                <Button
                    variant="contained"
                    color="darkviolet"
                    onClick={perform}
                    disabled={
                        data === undefined || data.length === 0 || isProcessing
                    }
                    marginBottom="3"
                >
                    {isProcessing ? "Submitting..." : "Submit Data"}
                </Button>
                <CreateFileUrlLink orgUsername={orgUsername} />
                <NavButton
                    variant="text"
                    color="plain"
                    className="my-4"
                    to={routePaths.appCustomizeBaselineDataAddDataSingle(
                        orgUsername,
                        appID
                    )}
                >
                    Quick create ground truth data
                </NavButton>
                <NavButton
                    variant="text"
                    color="plain"
                    marginBottom="3"
                    to={routePaths.appCustomizeBaselineDataAddDataCurl(
                        orgUsername,
                        appID
                    )}
                >
                    Create ground truth data programatically
                </NavButton>
            </div>
        )
    }
    return (
        <BaselineDataBase
            orgUsername={orgUsername}
            id={appID}
            title="Add ground-truth data"
            breadcrumbs={["Ground-truth Data"]}
            child={render}
            renderSidebar={renderSidebar}
        />
    )
}

const mapStateToProps = (
    state: State,
    { appID: id }: UploadProps
): StateProps => {
    return {
        input: state.client.dataFileExplorer.explorer[INPUT(id)],
        output: state.client.dataFileExplorer.explorer[OUTPUT(id)]
    }
}

const mapDispatchToProps = (
    dispatch: MainThunkDispatch,
    { appID, orgUsername }: UploadProps
): DispatchProps => {
    return {
        submit: async (
            data: UploadData,
            inputSchema: JSONSchema7,
            outputSchema: JSONSchema7
        ) => {
            await dispatch(
                batchCreateBaselineData(
                    appID,
                    data,
                    "ground-truth/new/input",
                    "ground-truth/new/label",
                    inputSchema,
                    outputSchema
                )
            )
            dispatch(clearDataFileExplorer(INPUT(appID)))
            dispatch(clearDataFileExplorer(OUTPUT(appID)))
            dispatch(
                push(routePaths.appCustomizeBaselineData(orgUsername, appID))
            )
        }
    }
}

export const BaselineDataUpload = connect(
    mapStateToProps,
    mapDispatchToProps
)(UploadIsolated)
